import * as React from 'react';
import FormItem from 'antd/lib/form/FormItem';
import Tooltip from 'antd/lib/tooltip';
import Input from 'antd/lib/input';
import GenericHoc from 'src/components/hoc/GenericHoc';
import SelectStakeholder from 'src/components/common/CommonSelect/SelectStakeholder';
import StakeholderIcon from './create-shipment.svg/stakeholder';
import OrderDetailsIcon from './create-shipment.svg/order-details';
import SelectPlant from 'src/components/common/CommonSelect/SelectPlant';
import Loader from 'src/components/common/Loader';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { getCustomFields } from 'src/api/shipmentExecution';
import { CustomFields } from 'src/Library/Types';
import { newShipmentThirdStyles } from './new-shipment.styles';
import { NewShipmentThirdProps, FormField, CurrentPage } from './new-shipment.types';
import { ShipmentFormFields, formItemLayout } from './new-shipment.constants';
import { isValidArray , formRules } from 'src/Library/Helper';
import { EximModules, ModesOfService, OrgTypes, StakeholderTypes } from 'src/Library/Constants';
import { MOVEMENT_TYPES } from '../../Container/ContainerUtils';
import { SELocationType } from '../SE.interfaces';
import { DatePicker, Select } from 'antd';

const NewShipmentThird = (props: NewShipmentThirdProps) => {

  const { classes, form, editData, formFieldConfig, hide, isBlNumberPresent, isFieldDiabled, setCustomFieldsMap, isParentOrg, isChildOrg, stakeholderAccountType } = props;

  const { getFieldDecorator } = form;

  const [loading, setLoading] = React.useState(false);
  const [customFields, setCustomFields] = React.useState<CustomFields[]>([]);

  const {
    MODE,
    PARENT,
    CHILD,
    FF,
    CHA,
    TRANSPORTER,
    CFS,
    ICD,
    BREAK_BULK,
    SURVEYOR,
    INTER_CARTING,
    PLANT,
    CUSTOMER,
    SUPPLIER,
    PURCHASE,
    SALES,
    INTERNAL,
    INVOICE,
    MOVEMENT,
    bookingNumber,
  } = ShipmentFormFields;

  const movementType = form.getFieldsValue()[MOVEMENT.key];
  const mode = form.getFieldValue(MODE.key);

  async function fetchCustomFields() {
    setLoading(true);
    const response = await getCustomFields({ moduleName: EximModules.EXECUTION });
    setCustomFields(response?.data || []);
    setCustomFieldsMap(response?.data || []);
    setLoading(false);
  }

  React.useEffect(() => {
    fetchCustomFields();
  }, []);


  const getLabel = (label: string, required = false) => {
    return <span
      className={classes.ellipses}
      style={{
        maxWidth: '90%',
        display: 'inline-block',
        margin: 0,
        padding: 0,
      }}
    >
      <Tooltip title={label}>
        {label}
      </Tooltip>
      {!isBlNumberPresent && required && <span className={classes.required}>
        *
      </span>}
    </span>;
  };

  const renderMultipleInput = (field: FormField, disabled?: boolean | undefined) => {
    const isRequired = !isBlNumberPresent && (formFieldConfig[field.configKey] ?? false);
    const rules = {...formRules.required};
    rules.required = isRequired
    const fieldKey = field.key;

    const getInitialValue = () => {
      if (!editData?.id) return undefined;
      const value = field.initialValue || editData[fieldKey];
      if (Array.isArray(value)) {
          return value;
      } else if (value) {
          return [value];
      }
      return undefined;
    };
    
    const initialValue = getInitialValue();
    
    return <FormItem
      label={getLabel(field.label, isRequired)}
      className={classes.formItem}
      {...formItemLayout}
    >
      {getFieldDecorator(field.key, {
        initialValue: initialValue,
        rules: [rules]
      })(<Select
        mode="tags"
        disabled={disabled}
        placeholder={field.placeholder}
        className={classes.input}
        notFoundContent={null}
        />)}
    </FormItem>;
  }

  const renderInput = (field: FormField, disabled?: boolean) => {
    const isRequired = !isBlNumberPresent && (formFieldConfig[field.configKey] ?? false);
    const rules = {...formRules.required};
    rules.required = isRequired
    return <FormItem
      label={getLabel(field.label, isRequired)}
      className={classes.formItem}
      {...formItemLayout}
    >
      {getFieldDecorator(field.key, {
        rules: [formRules.max, rules],
        initialValue: editData?.id ? field.initialValue || editData[field.key] : undefined,
      })(<Input
        disabled={disabled}
        placeholder={field.placeholder}
        className={classes.input}
        />)}
    </FormItem>;
  };

  const getStakeholderInitialValue = (field?: FormField) => {
    switch (field?.stakeholderType) {
      case OrgTypes.SUPPLIER:
        return isValidArray(editData?.supplierIds) ? editData?.supplierIds : (editData?.supplierId ? [editData?.supplierId] : undefined);
      case OrgTypes.CUSTOMER:
        return isValidArray(editData?.customerIds) ? editData?.customerIds : (editData?.customerId ? [editData?.customerId] : undefined);
      case OrgTypes.FF:
        return editData?.ffId;
      case OrgTypes.CHA:
        return editData?.chaId;
      case OrgTypes.TRANSPORTER:
        return editData?.transporterId;
      case OrgTypes.PARENT:
        return editData?.parentId;
      case OrgTypes.CHILD:
        return editData?.childId;
      case OrgTypes.CFS_CONTRACTOR:
        return editData?.cfsContractorId;
      case OrgTypes.ICD_CONTRACTOR:
        return editData?.icdContractorId;
      case OrgTypes.BREAK_BULK:
        return editData?.breakBulkId;
      case OrgTypes.SURVEYOR:
        return editData?.surveyorId;
      case OrgTypes.INTER_CARTING:
        return editData?.interCartingId;
      default: return undefined;
    }
  };

  const getPlantInitialValue = () => {
    return editData?.plantId;
  }

  const renderStakeholder = (field: FormField, isMultiple: boolean = false) => {
    const isRequired =  (!isBlNumberPresent && (formFieldConfig[field.configKey] ?? false));
    const rules = {...formRules.required};
    rules.required = isRequired
    let stakeholder;
    if (editData?.type === "IMPORT" && field.key === "supplierIds"){
      stakeholder = {
        key : editData?.stakeholder?.id,
        value : editData?.stakeholder?.name
      };
    }
    if (editData?.type === "EXPORT" && field.key === "supplierIds"){
      stakeholder = {
        key : editData?.customerId,
        value : editData?.customer?.name
      };
    }
    return <FormItem
      label={getLabel(field.label, isRequired)}
      className={classes.formItem}
      {...formItemLayout}
    >
      {getFieldDecorator(field.key, {
        initialValue: getStakeholderInitialValue(field) || stakeholder,
        rules: [rules]
      })(<SelectStakeholder
        apiOptions={{
          type: field.stakeholderType as string,
        }}
        disabled={isFieldDiabled('stakeholderDisabled', isRequired)}
        className={classes.input}
        placeholder={field.placeholder}
        mode={ isMultiple ? "multiple" : "default" }
      />)}
    </FormItem>;
  };

  const renderPlant = (field: FormField) => {
    const isRequired = !isBlNumberPresent && (formFieldConfig[field.configKey] ?? false);
    const rules = {...formRules.required};
    rules.required = isRequired
    const isCustomer = stakeholderAccountType === StakeholderTypes.CUSTOMER;
    const shipperId = isCustomer ? form.getFieldValue('shipperId') : undefined;
    
    return <FormItem
      label={getLabel(field.label, isRequired)}
      className={classes.formItem}
      {...formItemLayout}
    >
      {getFieldDecorator(field.key, {
        initialValue: getPlantInitialValue(),
        rules: [rules]
      })(<SelectPlant
        locationType={"PLANT" as SELocationType}
        shipperId={shipperId}
        className={classes.input}
        placeholder={field.placeholder}
      />)}
    </FormItem>;
  };

  const renderStakeholderTitle = () => {
    return <div className={classes.boxTitle}>
      <div className={classes.boxIcon}>
        <StakeholderIcon  className={classes.boxIcon}/>
      </div>
      <span>Stakeholders</span>
    </div>;
  };

  const renderStakeholderFields = () => {
    if (movementType !== MOVEMENT_TYPES.TRADE){
      const shipper = movementType === MOVEMENT_TYPES.IMPORT ? SUPPLIER : CUSTOMER;
    return <div  className={classes.boxFields}>
      <div className={classes.flexRow}>
        {renderStakeholder(shipper, true)}
        {renderStakeholder(FF)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(CHA)}
        {renderStakeholder(TRANSPORTER)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(CFS)}
        {renderStakeholder(ICD)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(BREAK_BULK)}
        {renderStakeholder(SURVEYOR)}
      </div>
      <div>
        {renderStakeholder(INTER_CARTING)}
      </div>
      <div>
      {/* if org has linked parent orgs, its is a child, then show this dropdown */}
      {isChildOrg && renderStakeholder(PARENT)}
      {/* if org has linked child orgs, its is a parent, then show this dropdown */}
      {isParentOrg && renderStakeholder(CHILD)}
      </div>
    </div>;
    } else {
      return <div  className={classes.boxFields}>
      <div className={classes.flexRow}>
        {renderStakeholder(SUPPLIER, true)}
        {renderStakeholder(CUSTOMER, true)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(FF)}
        {renderStakeholder(CHA)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(TRANSPORTER)}
        {renderStakeholder(CFS)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(ICD)}
        {renderStakeholder(BREAK_BULK)}
      </div>
      <div className={classes.flexRow}>
        {renderStakeholder(SURVEYOR)}
        {renderStakeholder(INTER_CARTING)}
      </div>
      {/* if org has linked parent orgs, its is a child, then show this dropdown */}
      {isChildOrg && renderStakeholder(PARENT)}
      {/* if org has linked child orgs, its is a parent, then show this dropdown */}
      {isParentOrg && renderStakeholder(CHILD)}
    </div>;
    }
    
  };

  const renderStakeholders = () => {
    return <div className={classes.box}>
      {renderStakeholderTitle()}
      {renderStakeholderFields()}
    </div>;
  };

  const renderOrderDetailsTitle = () => {
    return <div className={classes.boxTitle}>
      <div className={classes.boxIcon}>
        <OrderDetailsIcon  className={classes.boxIcon}/>
      </div>
      <span>Order Details</span>
    </div>;
  };

  function getCustomFieldInitialValue(item: CustomFields) {
    if (isValidArray(editData?.customFields)) {
      const field = editData?.customFields?.find(custom => custom.dbKey === item.dbKey);
      return field?.value || editData?.[item.dbKey];
    }
    return editData?.customFields?.[item.dbKey] || editData?.[item.dbKey];
  };

  function renderSelectOption(props: FormField) {
    const { key, placeholder, label, required, initialValue, optionsList = [], type } = props;
    let customfieldInputJsx = <div></div>;
    let fieldType = type || 'string';
    
    if (optionsList?.length) {
      fieldType = 'dropdown';
    }
    switch (fieldType) {
      case 'date': {
        customfieldInputJsx = (
          <DatePicker
            allowClear={true}
            format={['DD/MM/YYYY']}
            placeholder={placeholder}
            className={classes.input}
          />
        );
        break;
      }
      case 'dropdown': {
        customfieldInputJsx = (
          <Select allowClear placeholder={placeholder} className={classes.input}>
            {optionsList.map((item, indx) => {
              return <Select.Option value={item}>{item}</Select.Option>;
            })}
          </Select>
        );
        break;
      }
      case 'string':
      default: {
        customfieldInputJsx = <Input placeholder={placeholder} className={classes.input} />;
        break;
      }
    }

    return (
      <FormItem label={getLabel(label)} className={classes.formItem} {...formItemLayout}>
        {getFieldDecorator(key, { initialValue })(customfieldInputJsx)}
      </FormItem>
    );
  }

  const renderCustomFields = () => {
    return customFields?.map((item, idx) => {
      if (idx % 2 === 0) {
        const nextItem = customFields.length > idx + 1 ? customFields[idx + 1] : null;
        return (
          <div className={classes.flexRow}>
            {renderSelectOption({
              configKey: '',
              key: `customFields[${item.dbKey}]`,
              placeholder: item.description,
              label: item.description,
              required: false,
              initialValue: getCustomFieldInitialValue(item),
              optionsList: item?.dropdownValue ?? [],
              step: CurrentPage.THIRD,
              type: item?.type?.toLowerCase(),
            })}
            {nextItem &&
              renderSelectOption({
                configKey: '',
                key: `customFields[${nextItem.dbKey}]`,
                placeholder: nextItem.description,
                label: nextItem.description,
                required: false,
                initialValue: getCustomFieldInitialValue(nextItem),
                optionsList: nextItem?.dropdownValue ?? [],
                step: CurrentPage.THIRD,
                type: nextItem?.type?.toLowerCase(),
              })}
          </div>
        );
      } else return null;
    });
  };

  const renderOrderDetails = () => {
    return <div className={classes.boxFields}>
      <div className={classes.flexRow}>
        {renderMultipleInput(PURCHASE)}
        {renderInput(SALES)}
      </div>
      <div className={classes.flexRow}>
        {renderInput(INTERNAL)}
        {renderMultipleInput(INVOICE)}
      </div>
      <div className={classes.flexRow}>
        {renderPlant(PLANT)}
      </div>
      {renderCustomFields()}
    </div>;
  };

  const renderOrderInfo = () => {
    return <div className={classes.box}>
      {renderOrderDetailsTitle()}
      {renderOrderDetails()}
    </div>;
  };

  if (loading) {
    return <Loader />;
  }

  const isCustomer = props.stakeholderAccountType === StakeholderTypes.CUSTOMER;

  return <div className={classes.main} style={{display: hide ? 'none' : 'block'}}>
    {!isCustomer && renderStakeholders()}
    {renderOrderInfo()}
  </div>;
};

const options: HocOptions = {
  connectJss: {
    useJss: true,
    styleSheet: newShipmentThirdStyles,
  },
  ignoreWithAuth: true,
};

const StepThree = GenericHoc(options)(NewShipmentThird);
export default StepThree;
